import { useState } from 'react';
import {
  BookOpenIcon,
  CaretDownIcon,
  QuestionIcon,
} from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';

import {
  HELP_MENU_ORDER,
  useHelpMenuSlotTarget,
} from '@/components/common/HelpMenu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';

/**
 * When true, help items sit behind a collapsible "Help" section (starts closed).
 * Set to false to always show help actions, including in icon-only sidebar mode.
 * Flip to a feature flag here when we want this configurable per org.
 */
const HELP_MENU_COLLAPSIBLE = false;

function HelpMenuItems({
  setSlotEl,
  gt,
}: {
  setSlotEl: (el: HTMLUListElement | null) => void;
  gt: ReturnType<typeof useGT>;
}) {
  return (
    <SidebarMenu
      ref={setSlotEl}
      // Flex column with explicit `order` per item lets portaled slots sit
      // at their declared HELP_MENU_ORDER positions regardless of mount
      // order.
      className="flex flex-col"
    >
      <SidebarMenuItem style={{ order: HELP_MENU_ORDER.documentation }}>
        <SidebarMenuButton
          disabled
          tooltip={gt('Documentation - coming soon', {
            context:
              'tooltip on the disabled Documentation help menu item shown only when the sidebar is collapsed',
          })}
          aria-label={gt('Documentation (coming soon)', {
            context: 'aria label for the disabled Documentation menu item',
          })}
        >
          <BookOpenIcon />
          {/* Trailing label/badge block is hidden in icon mode so the
              SidebarMenuButton can center the icon inside its 32x32 shell. */}
          <span className="flex w-full items-center justify-between gap-2 group-data-[collapsible=icon]:hidden">
            <T context="sidebar help menu item label for documentation link">
              Documentation
            </T>
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              <T context="badge label on a feature that is not yet released">
                Coming soon
              </T>
            </span>
          </span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

function HelpSectionLabel({ collapsible }: { collapsible: boolean }) {
  const label = (
    <span className="flex items-center gap-2">
      <QuestionIcon aria-hidden="true" />
      <T context="sidebar section heading for help and support actions">Help</T>
    </span>
  );

  if (collapsible) {
    return (
      <CollapsibleTrigger asChild>
        <SidebarGroupLabel className="group/help cursor-pointer justify-between">
          {label}
          <CaretDownIcon
            className="transition-transform group-data-[state=closed]/help:-rotate-90"
            aria-hidden="true"
          />
        </SidebarGroupLabel>
      </CollapsibleTrigger>
    );
  }

  return <SidebarGroupLabel>{label}</SidebarGroupLabel>;
}

export function HelpMenuSection() {
  const [isOpen, setIsOpen] = useState(false);
  const setSlotEl = useHelpMenuSlotTarget();
  const gt = useGT();
  const menu = <HelpMenuItems setSlotEl={setSlotEl} gt={gt} />;

  return (
    // Drop the SidebarGroup padding in collapsed (icon) mode so the help
    // icons align with the navigation items in SidebarContent. SidebarFooter
    // already adds `p-2`, and stacking another `p-2` from the group shifts
    // the 32px buttons 8px to the right relative to the 48px sidebar.
    <SidebarGroup className="group-data-[collapsible=icon]:p-0">
      {HELP_MENU_COLLAPSIBLE ? (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <HelpSectionLabel collapsible />
          <CollapsibleContent>{menu}</CollapsibleContent>
        </Collapsible>
      ) : (
        <>
          <HelpSectionLabel collapsible={false} />
          {menu}
        </>
      )}
    </SidebarGroup>
  );
}
